/**
 * Python compiler wrapper using Skulpt via Web Worker (same approach as CodeEditor.tsx).
 *
 * Goals:
 * - Keep a simple public API: initialize, executeCode, executeCodeWithTestCases, isReady
 * - Use the same working Skulpt execution approach as CodeEditor.tsx
 * - Handle test case validation and result comparison
 * - Provide consistent interface with cppCompiler.ts
 */

interface ExecutionResult {
  success: boolean;
  output: string;
  error?: string;
  executionTime: number;
  compilationError?: string;
  runtimeError?: string;
  errorType?: 'compilation' | 'runtime' | 'execution';
}

export interface TestCase {
  input: string;
  expectedOutput: string;
  fullExecutableCode: string;
}

class PythonCompiler {
  // ——— Configuration ——————————————————————————————————————————————
  private static readonly SKULPT_URL = 'https://skulpt.org/js/skulpt.min.js';
  private static readonly SKULPT_STDLIB_URL = 'https://skulpt.org/js/skulpt-stdlib.js';
  private static readonly EXECUTION_TIMEOUT_MS = 6000;

  // ——— Runtime state ———————————————————————————————————————————————
  private isInitialized = false;

  /**
   * Initialize Python compiler (no-op for compatibility, Skulpt loads on-demand)
   */
  async initialize(): Promise<void> {
    // Skulpt loads on-demand in each execution, so no initialization needed
    this.isInitialized = true;
    console.log('[python] compiler initialized (Skulpt loads on-demand)');
  }

  /**
   * Execute Python code with input and function name
   */
  async executeCode(code: string, input: string = '', functionName: string = 'main'): Promise<ExecutionResult> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    const startTime = performance.now();

    console.log('[python] executeCode called with:');
    console.log('[python] - code:', code);
    console.log('[python] - input:', input);
    console.log('[python] - functionName:', functionName);

    try {
      const result = await this.executePythonInWorker(code, input, functionName);
      const executionTime = performance.now() - startTime;

      console.log('[python] executeCode result:', result);

      return {
        success: result.success,
        output: result.output,
        error: result.error,
        executionTime,
        errorType: result.error ? 'runtime' : undefined
      };

    } catch (err) {
      const executionTime = performance.now() - startTime;
      const errorMessage = err instanceof Error ? err.message : 'Execution failed';
      
      console.error('[python] executeCode error:', errorMessage);
      
      return {
        success: false,
        output: '',
        error: errorMessage,
        errorType: 'execution',
        executionTime,
      };
    }
  }

  /**
   * Execute Python code in a Web Worker using the same approach as CodeEditor.tsx
   */
  private executePythonInWorker(code: string, input: string, functionName: string): Promise<{ success: boolean; output: string; error?: string }> {
    return new Promise((resolve) => {
      // The code should already be executable (including function calls)
      // Just use it as-is, similar to how CodeEditor.tsx works
      const codeToExecute = code;

      // Create worker code that will load and run Skulpt (same as CodeEditor.tsx)
      const workerCode = `
        console.log('[python:worker] Starting execution...');
        console.log('[python:worker] Code to execute:', \`${codeToExecute}\`);
        
        self.importScripts('${PythonCompiler.SKULPT_URL}', '${PythonCompiler.SKULPT_STDLIB_URL}');
        
        // Function to handle file reads
        function builtinRead(x) {
          if (Sk.builtinFiles === undefined || Sk.builtinFiles["files"][x] === undefined) {
            throw "File not found: '" + x + "'";
          }
          return Sk.builtinFiles["files"][x];
        }
        
        // Output handler
        const output = [];
        
        // Configure Skulpt
        Sk.configure({
          output: function(text) { 
            console.log('[python:worker] Output:', text);
            output.push({type: "result", content: text});
            self.postMessage({type: "result", content: text});
          },
          read: builtinRead,
          __future__: Sk.python3,
          execLimit: 10000,
          killableWhile: true
        });
        
        // Run the Python code
        try {
          console.log('[python:worker] Executing Python code...');
          Sk.misceval.asyncToPromise(function() {
            return Sk.importMainWithBody("<stdin>", false, \`${codeToExecute
              .replace(/\\/g, "\\\\")
              .replace(/\`/g, "\\`")}\`, true);
          }).then(
            function() { 
              console.log('[python:worker] Execution completed successfully');
              self.postMessage({type: "done"}); 
            },
            function(err) { 
              console.error('[python:worker] Execution error:', err);
              self.postMessage({type: "error", content: err.toString()});
              self.postMessage({type: "done"}); // Ensure completion after error
            }
          );
        } catch(e) {
          console.error('[python:worker] Setup error:', e);
          self.postMessage({type: "error", content: e.toString()});
          self.postMessage({type: "done"}); // Ensure completion after error
        }
      `;

      // Create worker (same approach as CodeEditor.tsx)
      const blob = new Blob([workerCode], { type: "application/javascript" });
      const workerUrl = URL.createObjectURL(blob);
      const worker = new Worker(workerUrl);

      const output: any[] = [];
      let hasError = false;

      // Set timeout (same as CodeEditor.tsx)
      const timeoutId = setTimeout(() => {
        worker.terminate();
        URL.revokeObjectURL(workerUrl);
        resolve({ 
          success: false, 
          output: '', 
          error: "Execution timed out. Your code might contain an infinite loop." 
        });
      }, PythonCompiler.EXECUTION_TIMEOUT_MS);

      // Handle messages (same as CodeEditor.tsx)
      worker.onmessage = (event) => {
        if (event.data.type === "done") {
          clearTimeout(timeoutId);
          worker.terminate();
          URL.revokeObjectURL(workerUrl);
          
          if (hasError) {
            resolve({ success: false, output: '', error: output.find(item => item.type === 'error')?.content });
          } else {
            const resultOutput = output
              .filter(item => item.type === 'result')
              .map(item => item.content)
              .join('');
            resolve({ success: true, output: resultOutput });
          }
        } else if (event.data.type === "error") {
          hasError = true;
          output.push(event.data);
        } else if (event.data.type === "result") {
          output.push(event.data);
        }
      };

      // Handle errors (same as CodeEditor.tsx)
      worker.onerror = (error) => {
        clearTimeout(timeoutId);
        worker.terminate();
        URL.revokeObjectURL(workerUrl);
        resolve({ 
          success: false, 
          output: '', 
          error: "Worker error: " + error.message 
        });
      };
    });
  }

  /**
   * Create full executable code by injecting user function
   */
  private createFullCode(userCode: string, fullCode: string): string {
    return fullCode.replace(
      '// USER_FUNCTION_WILL_BE_INJECTED_HERE',
      userCode
    );
  }

  /**
   * Extract function name from the test case code
   */
  private extractFunctionName(fullCode: string): string | null {
    // Look for function definition patterns
    const functionMatch = fullCode.match(/def\s+(\w+)\s*\(/);
    if (functionMatch && functionMatch[1]) {
      return functionMatch[1];
    }
    
    // Look for lambda patterns
    const lambdaMatch = fullCode.match(/(\w+)\s*=\s*lambda/);
    if (lambdaMatch && lambdaMatch[1]) {
      return lambdaMatch[1];
    }
    
    return null;
  }

  /**
   * Clean output by removing ANSI codes and empty lines
   */
  private cleanOutput(output: string): string {
    const stripAnsi = (text: string) => text.replace(/\u001b\[[0-9;]*m/g, '');
    const cleanedOutput = stripAnsi(output || '')
      .split(/\r?\n/)
      .map(s => stripAnsi(s).trim())
      .filter(Boolean);

    return cleanedOutput.join('\n');
  }

  /**
   * Execute code with multiple test cases
   */
  async executeCodeWithTestCases(
    userCode: string,
    testCases: TestCase[]
  ): Promise<ExecutionResult[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    const results: ExecutionResult[] = [];

    for (const testCase of testCases) {
      try {
                // For Python, we need to create executable code like the working executePython function
        // The userCode should be the function definition, we'll call it with the input
        const trimmedCode = userCode.trim();
        
        // Try to extract function name from userCode, fallback to 'main'
        const functionName = this.extractFunctionName(userCode) || 'main';
        console.log('[python] Using function name:', functionName);
        console.log('[python] Test case input:', testCase.input);
        
        // Create executable code similar to CodeEditor.tsx executePython
        // Note: userCode should contain the function definition, we'll call it with the input
        const codeToExecute = `${trimmedCode}

# Test the function with input
result = ${functionName}(${testCase.input})
print(result)`;

        console.log('[python] Executing code:', codeToExecute);
        console.log('[python] Test case input:', testCase.input);
        
        const execResult = await this.executeCode(codeToExecute, testCase.input, functionName);
        const cleanedOutput = this.cleanOutput(execResult.output);
        execResult.output = cleanedOutput;
        results.push(execResult);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Execution failed for test case';
        results.push({
          success: false,
          output: '',
          error: errorMessage,
          errorType: 'execution',
          executionTime: 0,
        });
      }
    }
    
    return results;
  }

  /**
   * Check if compiler is ready
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Cleanup resources (no-op for this implementation)
   */
  destroy(): void {
    // No cleanup needed for this implementation
    this.isInitialized = false;
  }
}

// Export singleton
export const pythonCompiler = new PythonCompiler();
export default pythonCompiler;
